import { useState, type PointerEvent, useContext, useEffect } from 'react';
import { type XYPosition } from '@xyflow/react';
import { CanvasWrapperResizeObserverContext } from '../internal-context/CanvasWrapperDimensionsContext';
import { colors, Typography } from '@sweep-io/sweep-design';
import { getDimensions, getPosition } from './helper';
import { useSelectNodes } from '../node-changes-event-handlers/useSelectNodes';
import { CSS_Z_INDEX_CREATE_ELEMENT_CURSOR } from '../const/zIndex';

export const CreateFreeElementTool = ({
  onCreate,
  onCancel,
  cursorPlaceholderText,
  createOnClick,
}: {
  onCreate: (start: XYPosition, end: XYPosition) => void;
  onCancel: () => void;
  cursorPlaceholderText?: string;
  createOnClick?: boolean;
}) => {
  const selectNodes = useSelectNodes();
  const [start, setStart] = useState<XYPosition | null>(null);
  const [end, setEnd] = useState<XYPosition | null>(null);
  const [cursorPosition, setCursorPosition] = useState<XYPosition | null>(null);

  const { canvasWrapperRef } = useContext(CanvasWrapperResizeObserverContext);

  // Clear selection when the tool is mounted
  useEffect(() => {
    selectNodes([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Escape key to cancel creation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCancel]);

  const handlePointerDown = (e: PointerEvent) => {
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    setStart({ x: e.pageX, y: e.pageY });
  };

  const handlePointerMove = (e: PointerEvent) => {
    // Track cursor position for placeholder text
    if (cursorPlaceholderText) {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    }

    // Track dragging for preview
    if (e.buttons !== 1) return;
    setEnd({ x: e.pageX, y: e.pageY });
  };

  const handlePointerUp = (e: PointerEvent) => {
    if (!createOnClick && (!start || !end)) return;
    if (!start || !end) {
      onCreate({ x: e.pageX, y: e.pageY }, { x: e.pageX, y: e.pageY });
    } else {
      onCreate(start, end);
    }

    setStart(null);
    setEnd(null);
  };

  let rect: { position: XYPosition; dimension: { width: number; height: number } } | null = null;
  if (start && end) {
    const position = getPosition(start, end, canvasWrapperRef);
    rect = {
      position,
      dimension: getDimensions(start, end),
    };
  }

  return (
    <div
      className="nopan nodrag canvas-free-element-overlay cursor-crosshair"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {rect && (
        <div
          className="canvas-free-element-preview"
          style={{
            ...rect.dimension,
            transform: `translate(${rect.position.x}px, ${rect.position.y}px)`,
            border: `1px solid ${colors.blue[500]}`,
            pointerEvents: 'none',
          }}
        />
      )}
      {cursorPlaceholderText && cursorPosition && (
        <div
          style={{
            position: 'fixed',
            left: cursorPosition.x + 16,
            top: cursorPosition.y + 16,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: CSS_Z_INDEX_CREATE_ELEMENT_CURSOR,
          }}
        >
          <Typography variant="body-medium" color={colors.grey[500]}>
            {cursorPlaceholderText}
          </Typography>
        </div>
      )}
    </div>
  );
};
